import React from 'react';
import { observer } from 'mobx-react-lite';

import { SelectInput, TextInput } from '../input';
import {
  bwcPlatformOptions,
  mdtManufacturerOptions,
  mdtPurchaseWindowOptions,
  resellerOptions,
  rmsPlatformOptions,
} from '../../pages/agencySignupPage/agencySignupSchema';
import { AgencyTerminology, agencyTerminology } from '../agency/agencyTerminology';
import { AgencyDetailsForm, formatMdtPurchaseWindow, parseAgencyProgram } from './agencyDetailsForm';

type FieldsProps = {
  form: AgencyDetailsForm;
  onChange: (patch: Partial<AgencyDetailsForm>) => void;
  /** Omitted means Public Safety wording, which every pre-industry agency expects. */
  terms?: AgencyTerminology;
};

/**
 * The edit-agency-details form body, shared by the CLIPr-admin console and the sponsor dashboard.
 * `observer` because the caller hands over a MobX form object and every field is read in here.
 */
export const AgencyDetailsFields = observer(({ form, onChange, terms = agencyTerminology() }: FieldsProps) => (
  <>
    <TextInput
      id="agency-details-name"
      label={`${terms.Org} name`}
      value={form.agencyName}
      onChange={(evt) => onChange({ agencyName: evt.target.value })}
      required />
    <TextInput
      id="agency-details-sworn-officers"
      label={terms.SwornOfficers}
      type="number"
      min={1}
      max={100}
      value={form.swornOfficersCount}
      onChange={(evt) => onChange({ swornOfficersCount: evt.target.value })}
      required />
    <div className="form-row">
      <SelectInput
        id="agency-details-reseller"
        label="Reseller name"
        value={form.reseller}
        items={resellerOptions}
        onChange={(evt) => onChange({ reseller: evt.target.value })} />
      <SelectInput
        id="agency-details-mdt-window"
        label="Recent / upcoming MDT purchase"
        value={form.mdtPurchaseWindow}
        items={mdtPurchaseWindowOptions}
        onChange={(evt) => {
          const option = mdtPurchaseWindowOptions.find(({ value }) => value === evt.target.value);
          if (option)
            onChange({ mdtPurchaseWindow: option.value });
        }} />
    </div>
    <div className="form-row">
      <TextInput
        id="agency-details-reseller-poc"
        label="Reseller point of contact"
        value={form.resellerPocName}
        onChange={(evt) => onChange({ resellerPocName: evt.target.value })} />
      <TextInput
        id="agency-details-reseller-poc-email"
        label="Reseller point of contact email"
        type="email"
        value={form.resellerPocEmail}
        onChange={(evt) => onChange({ resellerPocEmail: evt.target.value })} />
    </div>
    <div className="form-row">
      <SelectInput
        id="agency-details-mdt-manufacturer"
        label="MDT manufacturer"
        value={form.mdtManufacturer}
        items={mdtManufacturerOptions}
        onChange={(evt) => onChange({ mdtManufacturer: evt.target.value })} />
      <TextInput
        id="agency-details-mdt-model"
        label="MDT model"
        value={form.mdtModel}
        onChange={(evt) => onChange({ mdtModel: evt.target.value })} />
    </div>
    <div className="form-row">
      <SelectInput
        id="agency-details-bwc"
        label="BWC platform"
        value={form.bwcPlatform}
        items={bwcPlatformOptions}
        onChange={(evt) => onChange({ bwcPlatform: evt.target.value })} />
      <SelectInput
        id="agency-details-rms"
        label="Records management system (RMS)"
        value={form.rmsPlatform}
        items={rmsPlatformOptions}
        onChange={(evt) => onChange({ rmsPlatform: evt.target.value })} />
    </div>
  </>
));

type GridProps = {
  classPrefix: string;
  agencyName: string;
  domain: string;
  swornOfficersCount: number;
  program: unknown;
  terms?: AgencyTerminology;
};

export const AgencyDetailsGrid = ({
  classPrefix, agencyName, domain, swornOfficersCount, program, terms = agencyTerminology(),
}: GridProps) => {
  const details = parseAgencyProgram(program);
  const item = (label: string, value: string) => (
    <div className={`${classPrefix}-detail-item`} key={label}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
  const mdt = [details.mdtManufacturer, details.mdtModel].filter(Boolean).join(' ');

  return (
    <div className={`${classPrefix}-details-grid`}>
      {item(`${terms.Org} name`, agencyName)}
      {item(`${terms.Org} domain`, domain)}
      {item(terms.SwornOfficers, String(swornOfficersCount))}
      {item('Reseller', details.reseller || 'Not set')}
      {item('Purchase window', formatMdtPurchaseWindow(details.mdtPurchaseWindow))}
      {item('MDT', mdt || 'Not set')}
      {item('BWC platform', details.bwcPlatform || 'Not set')}
      {item('RMS', details.rmsPlatform || 'Not set')}
    </div>
  );
};
